import globalMessage from "@/utils/globalMessage";
import React, { useState, useEffect } from "react";
import { Card, Row, Col, Statistic, Button, Form, InputNumber, Alert, Spin, Progress, Space } from "antd";
import ResponsiveModal from "@components/ResponsiveModal";
import { 
  ReloadOutlined, 
  EditOutlined,
  ThunderboltOutlined,
  ToolOutlined,
  HeartOutlined,
  SettingOutlined,
  RocketOutlined,
  DollarOutlined,
  CalculatorOutlined
} from "@ant-design/icons";
import Popconfirm from "@components/Feedback/Popconfirm";
import { api } from "@redux/api";

import moment from "moment";

const FacilityMetrics = ({ record }) => {
  const [loading, setLoading] = useState(false);
  const [metricsData, setMetricsData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editForm] = Form.useForm();

  // 获取性能指标数据
  const fetchMetrics = async () => {
    if (!record?.id) {
      console.error('No facility ID provided');
      return;
    }

    setLoading(true);
    try {
      const response = await api.getFacilityPerformanceMetrics(record.id);
      console.log('Performance Metrics Response:', response.data);
      setMetricsData(response.data);
    } catch (error) {
      console.error('Failed to fetch performance metrics:', error);
      message.error('Failed to fetch performance metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('FacilityMetrics record:', record);
    fetchMetrics();
  }, [record?.id]);

  // Recalculate performance metrics
  const handleRecalculate = async () => {
    if (!record?.id) {
      message.error('No facility ID available');
      return;
    }

    try {
      setCalculating(true);
      const response = await api.triggerAutoCalculation(record.id);

      // Update state with returned data
      if (response && response.data && response.data.data) {
        setMetricsData({
          ...metricsData,
          data: response.data.data
        });

        globalMessage.success({
          message: 'Calculation Complete',
          description: `Performance metrics have been successfully recalculated. Overall Score: ${response.data.overall_score?.toFixed(2)}`,
          placement: 'topRight',
          duration: 4
        });
      }
    } catch (error) {
      console.error('Recalculation failed:', error);
      globalMessage.error({
        message: 'Calculation Failed',
        description: error.response?.data?.message || error.message || 'Failed to recalculate performance metrics',
        placement: 'topRight'
      });
    } finally {
      setCalculating(false);
    }
  };


  // Get progress bar color
  const getProgressColor = (score) => {
    if (score >= 8) return '#52c41a'; // green
    if (score >= 6) return '#1890ff'; // blue
    if (score >= 4) return '#faad14'; // orange
    return '#ff4d4f'; // red
  };

  // Open single field edit modal
  const handleOpenEditModal = (fieldName, fieldLabel, currentValue) => {
    setEditingField({ name: fieldName, label: fieldLabel });
    editForm.setFieldsValue({ [fieldName]: currentValue });
    setEditModalVisible(true);
  };

  // Submit single field update
  const handleEditSubmit = async () => {
    try {
      const values = await editForm.validateFields();
      setSubmitting(true);

      const metricsId = metricsData.data.id;

      // Update single field
      const response = await api.updatePerformanceMetrics(metricsId, values);

      // Update state with returned data instead of fetching again
      if (response && response.data) {
        setMetricsData({
          ...metricsData,
          data: response.data
        });
      }

      message.success(`${editingField.label} updated successfully`);
      setEditModalVisible(false);
    } catch (error) {
      console.error('Update failed:', error);
      if (error.errorFields) {
        message.error('Please check form inputs');
      } else {
        message.error(error.response?.data?.message || 'Failed to update metric');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Render metric card
  const renderMetricCard = (title, value, icon, color) => {
    const score = value || 0;
    return (
      <Card>
        <Statistic
          title={title}
          value={score.toFixed(1)}
          prefix={icon}
          valueStyle={{ color }}
          // suffix="/ 10"
        />
        <Progress
          percent={score * 10}
          strokeColor={getProgressColor(score)}
          showInfo={false}
          style={{ marginTop: 8 }}
        />
      </Card>
    );
  };

  // Render editable metric card
  const renderEditableMetricCard = (title, value, icon, color, fieldName) => {
    // null/undefined 表示尚未设置（后端 comfort/adoption 手动维度可为 null），显示 "Not set" 而非 0.0
    const hasValue = value !== null && value !== undefined;
    const score = hasValue ? value : 0;
    return (
      <Card 
        hoverable
        onClick={() => handleOpenEditModal(fieldName, title, hasValue ? score : undefined)}
        style={{ cursor: 'pointer', position: 'relative' }}
      >
        <EditOutlined 
          style={{ 
            position: 'absolute', 
            top: 12, 
            right: 12, 
            fontSize: 16,
            color: '#1890ff'
          }} 
        />
        <Statistic
          title={title}
          value={hasValue ? score.toFixed(1) : 'Not set'}
          prefix={icon}
          valueStyle={hasValue ? { color } : { color: '#bfbfbf' }}
          // suffix="/ 10"
        />
        <Progress
          percent={hasValue ? score * 10 : 0}
          strokeColor={hasValue ? getProgressColor(score) : '#f0f0f0'}
          showInfo={false}
          style={{ marginTop: 8 }}
        />
      </Card>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" tip="Loading..." />
      </div>
    );
  }

  if (!metricsData?.data) {
    return (
      <div style={{ padding: '20px' }}>
        <Alert
          message="No Data Available"
          description="No performance metrics data available for this facility"
          type="info"
          showIcon
        />
        </div>
    );
  }

  const data = metricsData.data;
  const performanceBreakdown = metricsData.performance_breakdown || [];

  return (
    <div style={{ padding: '24px' }}>
      {/* Page title and action buttons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
          Facility Performance Metrics - {record?.name || data.facility_name}
        </h2>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchMetrics} loading={loading}>
            Refresh
          </Button>
          <Popconfirm
            title="Recalculate Performance Metrics"
            description="This will recalculate all auto-computed metrics (Energy Efficiency, Equipment Condition, Service Efficiency, and Cost Efficiency). Continue?"
            onConfirm={handleRecalculate}
            okText="Yes, Recalculate"
            cancelText="Cancel"
            placement="bottomRight"
          >
            <Button 
              type="primary" 
              icon={<CalculatorOutlined />} 
              loading={calculating}
            >
              Recalculate Metrics
            </Button>
          </Popconfirm>
        </Space>
      </div>

      {/* Overall Score */}
      <Card style={{ marginBottom: 24, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div style={{ textAlign: 'center', color: 'white' }}>
          <div style={{ fontSize: '16px', marginBottom: 8, opacity: 0.9 }}>Overall Score</div>
          <div style={{ fontSize: '48px', fontWeight: 'bold', marginBottom: 8 }}>
            {(metricsData?.data?.overall_score || 0)}
          </div>
          <div style={{ fontSize: '14px', opacity: 0.8 }}>
            Last Updated: {moment(metricsData?.data?.last_updated).format('YYYY-MM-DD HH:mm')}
          </div>
        </div>
      </Card>

      {/* 6 Core Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={8}>
          {renderMetricCard(
            'Energy Efficiency',
            data.energy_efficiency,
            <ThunderboltOutlined />,
            '#faad14'
          )}
        </Col>
        <Col xs={24} sm={12} lg={8}>
          {renderMetricCard(
            'Equipment Condition',
            data.equipment_condition,
            <ToolOutlined />,
            '#1890ff'
          )}
        </Col>
        <Col xs={24} sm={12} lg={8}>
          {renderEditableMetricCard(
            'Occupant Comfort',
            data.occupant_comfort,
            <HeartOutlined />,
            '#eb2f96',
            'occupant_comfort'
          )}
        </Col>
        <Col xs={24} sm={12} lg={8}>
          {renderMetricCard(
            'Service Efficiency',
            data.service_efficiency,
            <SettingOutlined />,
            '#13c2c2'
          )}
        </Col>
        <Col xs={24} sm={12} lg={8}>
          {renderEditableMetricCard(
            'Technology Adoption',
            data.technology_adoption,
            <RocketOutlined />,
            '#722ed1',
            'technology_adoption'
          )}
        </Col>
        <Col xs={24} sm={12} lg={8}>
          {renderMetricCard(
            'Cost Efficiency',
            data.cost_efficiency,
            <DollarOutlined />,
            '#52c41a'
          )}
        </Col>
      </Row>

      {/* Single Field Edit Modal */}
      <ResponsiveModal
        title={`Edit ${editingField?.label || 'Metric'}`}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={handleEditSubmit}
        confirmLoading={submitting}
        width={500}
        okText="Save"
        cancelText="Cancel"
      >
        <Form
          form={editForm}
          layout="vertical"
                        autoComplete="off">
          <Alert
            message="Note"
            description="Enter a score between 0-10 (supports 1 decimal place)"
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Form.Item
            name={editingField?.name}
            label={`${editingField?.label} Score`}
            rules={[
              { required: true, message: 'Please enter a score' },
              { type: 'number', min: 0, max: 10, message: 'Score must be between 0-10' }
            ]}
          >
            <InputNumber
              min={0}
              max={10}
              step={0.1}
              placeholder="Enter score between 0-10"
              style={{ width: '100%' }}
              autoFocus
            />
          </Form.Item>
        </Form>
      </ResponsiveModal>
    </div>
  );
};

export default FacilityMetrics;
